import { useRef, useState } from "react";
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  useMotionTemplate,
} from "framer-motion";
import { RotateCw } from "lucide-react";

const PLAYER_IMG = "/images/bigcal-card.jpg";

export default function ToppsCard({ player, career, pitching }) {
  const ref = useRef(null);
  const [flipped, setFlipped] = useState(false);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);

  const rotateX = useSpring(useTransform(my, [0, 1], [10, -10]), {
    stiffness: 140,
    damping: 18,
  });
  const rotateY = useSpring(useTransform(mx, [0, 1], [-12, 12]), {
    stiffness: 140,
    damping: 18,
  });
  const glareX = useTransform(mx, [0, 1], ["20%", "80%"]);
  const glare = useMotionTemplate`radial-gradient(circle at ${glareX} 18%, rgba(255,255,255,0.16), transparent 55%)`;

  const handleMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };
  const reset = () => {
    mx.set(0.5);
    my.set(0.5);
  };

  const careerRows = [
    ["AVG", career.AVG, "text-neon"],
    ["OBP", career.OBP, "text-neon"],
    ["OPS", career.OPS, "text-hotpink"],
    ["SLG", career.SLG, "text-hotpink"],
    ["H", career.H, "text-neonpurple"],
    ["RBI", career.RBI, "text-neonpurple"],
    ["R", career.R, "text-white"],
    ["SB", career.SB, "text-white"],
  ];

  return (
    <div style={{ perspective: 1200 }} className="select-none">
      <motion.div
        ref={ref}
        data-testid="topps-card"
        onMouseMove={handleMove}
        onMouseLeave={reset}
        onClick={() => setFlipped(!flipped)}
        style={{ rotateX, rotateY }}
        className="relative aspect-[5/7] w-[300px] cursor-pointer sm:w-[350px]"
      >
        <motion.div
          animate={{ rotateY: flipped ? 180 : 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          style={{ transformStyle: "preserve-3d" }}
          className="absolute inset-0"
        >
          {/* FRONT */}
          <div
            data-testid="topps-card-front"
            style={{ backfaceVisibility: "hidden" }}
            className="absolute inset-0 flex flex-col overflow-hidden rounded-2xl border-2 border-neon bg-panel shadow-[0_0_60px_rgba(57,255,20,0.15)]"
          >
            <motion.div
              style={{ background: glare }}
              className="pointer-events-none absolute inset-0 z-20"
            />
            <img
              src="/images/hat-cutout.webp"
              alt=""
              data-testid="card-hat-sticker"
              className="absolute left-2 top-2 z-30 w-16 -rotate-12 drop-shadow-[0_8px_18px_rgba(57,255,20,0.45)] transition-transform duration-500 hover:rotate-0 sm:w-20"
            />
            <div className="flex items-center justify-between px-4 pt-3">
              <span className="font-jbmono text-[10px] uppercase tracking-[0.25em] text-neon">
                {player.team}
              </span>
              <span className="font-jbmono text-[10px] tracking-[0.25em] text-white/40">
                2026 SERIES
              </span>
            </div>

            <div className="relative mx-3 mt-3 flex-1 overflow-hidden rounded-xl border border-white/10">
              <img
                src={PLAYER_IMG}
                alt="Big Cal cartoon card art"
                data-testid="card-action-photo"
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/90 to-transparent" />
              <span className="absolute right-2 top-2 rounded-full bg-hotpink px-2.5 py-1 font-jbmono text-[9px] font-semibold tracking-[0.2em] text-black">
                BIG HITTER
              </span>
            </div>

            <div className="px-4 pt-3">
              <div className="flex items-end justify-between">
                <h3
                  data-testid="topps-card-name"
                  className="font-bebas text-5xl leading-none tracking-wide"
                >
                  BIG <span className="text-neon">CAL</span>
                </h3>
                <span className="font-spray text-4xl leading-none text-hotpink drop-shadow-[0_0_15px_rgba(255,46,147,0.5)]">
                  #50
                </span>
              </div>
              <div className="mt-1 flex items-center justify-between font-jbmono text-[10px] tracking-[0.25em] text-white/50">
                <span data-testid="topps-card-positions">
                  {player.positions.join(" / ")} · BATS {player.bats} · THROWS{" "}
                  {player.throws}
                </span>
                <span className="text-neonpurple">AGE {player.age}</span>
              </div>
            </div>

            <div className="mt-auto">
              <div className="mt-3 flex border-t border-white/10">
                {[
                  ["AVG", career.AVG, "text-neon"],
                  ["SB", career.SB, "text-hotpink"],
                  ["OPS", career.OPS, "text-neonpurple"],
                ].map(([label, value, color]) => (
                  <div
                    key={label}
                    className="flex-1 border-r border-white/10 px-3 py-2 text-center last:border-r-0"
                  >
                    <p className={`font-bebas text-2xl leading-none ${color}`}>
                      {value}
                    </p>
                    <p className="mt-1 font-jbmono text-[8px] tracking-[0.3em] text-white/40">
                      {label}
                    </p>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between px-4 py-1.5">
                <span className="font-jbmono text-[8px] tracking-[0.3em] text-white/30">
                  BIG CAL · ROOKIE CLASS
                </span>
                <span className="flex items-center gap-1 font-jbmono text-[8px] tracking-[0.2em] text-neon/70">
                  <RotateCw className="h-2.5 w-2.5" />
                  TAP TO FLIP
                </span>
              </div>
              <div className="flex h-1.5">
                <div className="flex-1 bg-neon" />
                <div className="flex-1 bg-neonpurple" />
                <div className="flex-1 bg-hotpink" />
              </div>
            </div>
          </div>

          {/* BACK */}
          <div
            data-testid="topps-card-back"
            style={{
              backfaceVisibility: "hidden",
              transform: "rotateY(180deg)",
            }}
            className="absolute inset-0 flex flex-col overflow-hidden rounded-2xl border-2 border-hotpink bg-panel p-4 shadow-[0_0_60px_rgba(255,46,147,0.15)]"
          >
            <div className="flex items-center justify-between font-jbmono text-[9px] tracking-[0.25em] text-white/40">
              <span className="text-hotpink">CAREER LINE</span>
              <span>THRU SPRING 2026</span>
            </div>
            <img
              src="/images/logo-cutout.webp"
              alt=""
              data-testid="card-back-logo"
              className="absolute right-3 top-9 z-10 w-14 rotate-6 drop-shadow-[0_6px_18px_rgba(255,46,147,0.45)] transition-transform duration-500 hover:rotate-0"
            />
            <h4 className="mt-2 font-bebas text-3xl leading-none tracking-wide">
              BIG <span className="text-neon">CAL</span>{" "}
              <span className="text-hotpink">#50</span>
            </h4>
            <p className="mt-1 font-jbmono text-[9px] uppercase tracking-[0.2em] text-white/40">
              {player.team} · {player.positions.join(" / ")}
            </p>

            <div className="relative mt-3 h-[76px] shrink-0 overflow-hidden rounded-lg border border-white/10">
              <img
                src="/images/bigcal-card.jpg"
                alt="Shoreline All-Stars artwork"
                data-testid="card-back-art"
                className="h-full w-full object-cover object-[center_28%]"
              />
              <span className="absolute bottom-1 right-2 font-jbmono text-[7px] tracking-[0.25em] text-white/80">
                THE ARTWORK
              </span>
            </div>

            <div className="mt-3 grid grid-cols-4 gap-1.5">
              {careerRows.map(([label, value, color]) => (
                <div
                  key={label}
                  className="rounded-lg border border-white/10 bg-black/40 px-1.5 py-2 text-center"
                >
                  <p className={`font-bebas text-lg leading-none ${color}`}>
                    {value}
                  </p>
                  <p className="mt-1 font-jbmono text-[7px] tracking-[0.2em] text-white/40">
                    {label}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-3 rounded-lg border border-neonpurple/30 bg-black/40 px-3 py-2">
              <p className="font-jbmono text-[8px] tracking-[0.3em] text-neonpurple">
                ON THE MOUND
              </p>
              <p className="mt-1 font-bebas text-lg leading-tight tracking-wide text-white/90">
                {pitching.IP} IP · {pitching.SO} K · {pitching.ERA} ERA
              </p>
            </div>

            <div className="mt-3">
              <p className="font-jbmono text-[8px] tracking-[0.3em] text-neon">
                SCOUTING REPORT
              </p>
              <p className="mt-1.5 text-[11px] leading-relaxed text-white/60">
                {player.bio}
              </p>
            </div>

            <div className="mt-auto">
              <div className="flex items-center justify-between border-t border-white/10 pt-2 font-jbmono text-[8px] tracking-[0.2em] text-white/40">
                <span>
                  BATS {player.bats} · THROWS {player.throws} · AGE{" "}
                  {player.age}
                </span>
                <span className="text-white/25">EST. SPRING 2023</span>
              </div>
              <div className="mt-2 flex h-1.5 rounded-full">
                <div className="flex-1 rounded-l-full bg-hotpink" />
                <div className="flex-1 bg-neonpurple" />
                <div className="flex-1 rounded-r-full bg-neon" />
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}
